// The room pages: which component each room's tab shows, what it puts in the left column and the header, and its Help.
// A page is a file under rooms/<room>/<page>.tsx whose exports are a `RoomPageModule`; each room's index.ts re-exports
// its files (`export * as corkboard from './corkboard.tsx';`) and rooms/index.ts registers every room's index. The
// shell reads only this registry, so adding a page never changes the shell (README.md). A page the Mac has that no file
// registers yet shows a placeholder.
import type { ComponentType } from 'react';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { PlaceholderPage } from './Placeholder.tsx';
import { isRoom, ROOMS, type RoomDef, type RoomId } from './rooms.ts';

/** What a page file exports. */
export interface RoomPageModule {
  room: RoomId;
  /** The URL segment (`/p/<project>/<room>/<page>`): lowercase letters, digits and hyphens, unique in the room. */
  page: string;
  /** The tab's name. */
  title: string;
  /** The page, in the panel. */
  component: ComponentType<PageProps>;
  /** Its own left column, in place of the room's (the contents, the Story column, the projects). */
  column?: ComponentType<PageProps>;
  /** The column's name (the phone shows it on the button that opens the column). */
  columnTitle?: string;
  /** Controls at the right of the room's tabs (a phone puts them on a second row). */
  headerControls?: ComponentType<PageProps>;
  /** A Help article id (a file name in Apps/Shared/Help, without `.md`). */
  help?: string;
  /** About an item in the contents: the URL carries the node, and the inspector (⌥⌘I) is offered. */
  takesNode?: boolean;
  /** Reached from a menu, not a tab. */
  hidden?: boolean;
  /** Where a page the Mac doesn't have goes among the room's tabs (after the Mac's pages; lower first). */
  order?: number;
}

export interface RoomPage extends Required<Pick<RoomPageModule, 'room' | 'page' | 'title' | 'component'>> {
  column?: ComponentType<PageProps>;
  columnTitle?: string;
  headerControls?: ComponentType<PageProps>;
  help?: string;
  takesNode: boolean;
  hidden: boolean;
  /** The tab it's gathered under, with the room's other pages of that group (rooms.ts). */
  group?: string;
  /** No file registers it yet: the placeholder shows. */
  placeholder: boolean;
  /** Its place among the room's pages. */
  rank: number;
}

const SEGMENT = /^[a-z][a-z0-9-]{0,39}$/;

/** A module namespace (or object) checked as a page, with a message naming what's wrong. */
export function asPageModule(value: unknown, where = 'a room page'): RoomPageModule {
  const m = value as Partial<RoomPageModule> | null;
  const fail = (why: string): never => {
    throw new Error(`${where}: ${why}`);
  };
  if (!m || typeof m !== 'object') fail('not a module');
  if (typeof m!.room !== 'string' || !isRoom(m!.room)) fail(`room must be one of ${ROOMS.map((r) => r.id).join(', ')}`);
  if (typeof m!.page !== 'string' || !SEGMENT.test(m!.page)) fail('page must be a lowercase URL segment');
  if (typeof m!.title !== 'string' || !m!.title.trim()) fail('title is missing');
  if (typeof m!.component !== 'function' && (typeof m!.component !== 'object' || m!.component === null)) fail('component is missing');
  for (const k of ['column', 'headerControls'] as const) {
    const c = m![k];
    if (c !== undefined && typeof c !== 'function' && (typeof c !== 'object' || c === null)) fail(`${k} must be a component`);
  }
  return m as RoomPageModule;
}

export class Registry {
  private readonly byKey = new Map<string, RoomPage>();

  constructor(
    readonly rooms: readonly RoomDef[],
    /** What a page no file registers shows (it reads which page from `PageProps.page`). */
    placeholder: ComponentType<PageProps>,
  ) {
    for (const room of rooms) {
      room.pages.forEach((p, i) => {
        this.byKey.set(`${room.id}/${p.page}`, {
          room: room.id,
          page: p.page,
          title: p.title,
          component: placeholder,
          help: p.help,
          takesNode: !!p.takesNode,
          hidden: false,
          group: p.group,
          placeholder: true,
          rank: i,
        });
      });
    }
  }

  /** Registers pages. One already registered by a file (not a placeholder) can't be registered twice. */
  register(...modules: unknown[]): void {
    for (const value of modules) {
      const m = asPageModule(value);
      const key = `${m.room}/${m.page}`;
      const had = this.byKey.get(key);
      if (had && !had.placeholder) throw new Error(`${key} is registered twice`);
      const room = this.rooms.find((r) => r.id === m.room)!;
      const canonical = room.pages.find((p) => p.page === m.page);
      this.byKey.set(key, {
        room: m.room,
        page: m.page,
        title: m.title,
        component: m.component,
        column: m.column,
        columnTitle: m.columnTitle,
        headerControls: m.headerControls,
        help: m.help ?? canonical?.help,
        takesNode: m.takesNode ?? !!canonical?.takesNode,
        hidden: !!m.hidden,
        group: canonical?.group,
        placeholder: false,
        rank: had ? had.rank : room.pages.length + (m.order ?? 100),
      });
    }
  }

  /** Registers every page a room's index re-exports (`export * as x from './x.tsx'`); each must be of that room. */
  registerRoom(room: RoomId, index: Record<string, unknown>): void {
    for (const [name, value] of Object.entries(index)) {
      const m = asPageModule(value, `rooms/${room}/index.ts ▸ ${name}`);
      if (m.room !== room) throw new Error(`rooms/${room}/index.ts ▸ ${name}: its room is “${m.room}”`);
      this.register(m);
    }
  }

  /** Every page of a room in order, hidden ones included. */
  all(room: RoomId): RoomPage[] {
    return [...this.byKey.values()].filter((p) => p.room === room).sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title));
  }

  /** The room's tabs. */
  tabs(room: RoomId): RoomPage[] {
    return this.all(room).filter((p) => !p.hidden);
  }

  find(room: RoomId, page: string): RoomPage | null {
    return this.byKey.get(`${room}/${page}`) ?? null;
  }

  /** The page a room opens on first. */
  first(room: RoomId): RoomPage {
    return this.tabs(room)[0] ?? this.all(room)[0];
  }

  /** A room with one tab leaves its page out of URLs (`/p/<project>/write/<node>`). */
  singlePage(room: RoomId): boolean {
    return this.tabs(room).length === 1;
  }
}

let shared: Registry | null = null;

/** The app's registry (rooms/index.ts fills it). */
export function registry(): Registry {
  shared ??= new Registry(ROOMS, PlaceholderPage);
  return shared;
}

/** Registers pages in the app's registry. */
export function register(...modules: unknown[]) {
  registry().register(...modules);
}
